// Purpose: Error boundary component for graceful error handling
// Audience: Any component that needs error resilience
// Features:
// - Catches JavaScript errors in component tree
// - Displays fallback UI instead of white screen
// - Logs errors for debugging
// - Provides retry functionality

import React from 'react';
import PropTypes from 'prop-types';
import { FaExclamationTriangle, FaRedo } from 'react-icons/fa';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Report to error tracking service if available
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <FaExclamationTriangle />
            </div>
            <h3 className="error-boundary-title">
              {this.props.title || 'Something went wrong'}
            </h3>
            <p className="error-boundary-message">
              {this.props.message || 'An unexpected error occurred while displaying this content.'}
            </p>
            
            {this.props.showRetry && (
              <button 
                className="error-boundary-retry-btn"
                onClick={this.handleRetry}
                type="button"
                aria-label="Retry loading content"
              >
                <FaRedo aria-hidden="true" />
                Try Again
              </button>
            )}

            {this.props.showDetails && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details</summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  showRetry: PropTypes.bool,
  showDetails: PropTypes.bool,
  onError: PropTypes.func
};

ErrorBoundary.defaultProps = {
  showRetry: true,
  showDetails: false
};

export default ErrorBoundary;
